/**
 * CR310: Label Resolution Utility
 * CR313: Fixed precedence - deal-type defaults always win over variant pollution
 * CR450: Canonical component-scoped keys + legacy alias bridge
 * 
 * Resolves effective labels with deterministic precedence:
 * 1. draftOverrides.labels (preview-only)
 * 2. overrides_json.labels (user customizations)
 * 3. Legacy bridge: old keys → canonical keys
 * 4. DEFAULT_LABELS_BY_DEAL_TYPE[dealType] (deterministic deal-type defaults)
 * 
 * IMPORTANT: template_json.labels is intentionally NOT in the chain.
 * This prevents variant-level labels from bleeding across deal types.
 * 
 * Also tracks label sources for debug inspector.
 */

import { 
  DEFAULT_LABELS_BY_DEAL_TYPE, 
  DealTypeKey, 
  CanonicalLabelKey,
  CANONICAL_LABEL_KEYS,
  LEGACY_TO_CANONICAL,
  normalizeDealType 
} from "./defaultLabels";

// ================================================================
// TYPES
// ================================================================

type Labels = Record<string, string | undefined> | null | undefined;

export type LabelSource = "default" | "template" | "overrides" | "draft" | "legacy_bridge";

export interface LabelWithSource {
  value: string;
  source: LabelSource;
}

export interface ResolvedLabelsResult {
  /** Effective label values (merged) — keyed by canonical keys */
  labels: Record<string, string>;
  /** Source tracking for each label key */
  sources: Record<string, LabelSource>;
  /** CR313: Unknown keys found in overrides (for debug) */
  unknownKeys: string[];
  /** CR313: Deal type used for resolution */
  dealType: DealTypeKey;
  /** CR450: Whether any legacy keys were used (diagnostics) */
  usedLegacyKeys: string[];
}

// ================================================================
// MAIN RESOLVER
// ================================================================

/**
 * CR450: Resolve effective labels with canonical component-scoped keys.
 * 
 * Precedence (highest → lowest):
 * 1. draftLabels (preview-only)
 * 2. overridesLabels (user customizations)
 * 3. Legacy bridge (old keys → canonical keys)
 * 4. Deal-type defaults (deterministic per deal type)
 */
export function resolveEffectiveLabels(args: {
  dealType: string;
  templateLabels?: Labels;
  overridesLabels?: Labels;
  draftLabels?: Labels;
}): ResolvedLabelsResult {
  const { dealType, overridesLabels, draftLabels } = args;
  // NOTE: templateLabels intentionally ignored to prevent variant pollution
  
  const normalizedDealType = normalizeDealType(dealType);
  const defaults = DEFAULT_LABELS_BY_DEAL_TYPE[normalizedDealType];
  
  // Initialize with deal-type defaults (lowest priority) using canonical keys
  const labels: Record<string, string> = { ...defaults };
  const sources: Record<string, LabelSource> = {};
  const unknownKeys: string[] = [];
  const usedLegacyKeys: string[] = [];
  
  // Track default sources
  for (const key of CANONICAL_LABEL_KEYS) {
    sources[key] = "default";
  }
  
  // CR450: Process overrides — accept both canonical and legacy keys
  if (overridesLabels) {
    for (const [key, value] of Object.entries(overridesLabels)) {
      if (!value || typeof value !== "string" || value.trim() === "") continue;
      
      // Check if this is a canonical key
      if (CANONICAL_LABEL_KEYS.includes(key as CanonicalLabelKey)) {
        labels[key] = value;
        sources[key] = "overrides";
      }
      // Check if this is a legacy key we can map
      else if (key in LEGACY_TO_CANONICAL) {
        const canonicalKey = LEGACY_TO_CANONICAL[key];
        // Only apply if canonical key hasn't been explicitly set by overrides
        if (sources[canonicalKey] !== "overrides") {
          labels[canonicalKey] = value;
          sources[canonicalKey] = "legacy_bridge";
          usedLegacyKeys.push(key);
        }
      } else {
        unknownKeys.push(key);
      }
    }
  }
  
  // CR450: Process drafts — accept both canonical and legacy keys
  if (draftLabels) {
    for (const [key, value] of Object.entries(draftLabels)) {
      if (!value || typeof value !== "string" || value.trim() === "") continue;
      
      if (CANONICAL_LABEL_KEYS.includes(key as CanonicalLabelKey)) {
        labels[key] = value;
        sources[key] = "draft";
      } else if (key in LEGACY_TO_CANONICAL) {
        const canonicalKey = LEGACY_TO_CANONICAL[key];
        labels[canonicalKey] = value;
        sources[canonicalKey] = "draft";
        usedLegacyKeys.push(key);
      }
    }
  }
  
  // Also populate legacy keys in the output for backward compat with renderers
  // that still read context.labels.reward_title etc.
  for (const canonicalKey of CANONICAL_LABEL_KEYS) {
    const legacyMap: Record<string, string> = {
      value_box_title: "value_title",
      reward_box_title: "reward_title",
      qualifying_spend_box_title: "qualifying_spend_title",
      eligibility_box_title: "eligibility_title",
    };
    const legacyKey = legacyMap[canonicalKey];
    if (legacyKey && labels[canonicalKey]) {
      labels[legacyKey] = labels[canonicalKey];
      // Also set effective_value_title for legacy compat
      if (canonicalKey === "value_box_title") {
        labels["effective_value_title"] = labels[canonicalKey];
      }
    }
  }
  
  return { labels, sources, unknownKeys, dealType: normalizedDealType, usedLegacyKeys };
}

/**
 * Simple label resolver that just returns merged labels (no source tracking).
 * Use this when you don't need debug info.
 */
export function resolveLabelsSimple(args: {
  dealType: string;
  templateLabels?: Labels;
  overridesLabels?: Labels;
  draftLabels?: Labels;
}): Record<string, string> {
  return resolveEffectiveLabels(args).labels;
}

/**
 * Get a single effective label value
 */
export function resolveLabel(args: {
  dealType: string;
  labelKey: CanonicalLabelKey;
  templateLabels?: Labels;
  overridesLabels?: Labels;
  draftLabels?: Labels;
}): string {
  const { labelKey, ...rest } = args;
  const { labels } = resolveEffectiveLabels(rest);
  return labels[labelKey] || labelKey;
}

/**
 * Get source display name for UI
 */
export function getLabelSourceDisplayName(source: LabelSource): string {
  const names: Record<LabelSource, string> = {
    default: "Default",
    template: "Template",
    overrides: "User Override",
    draft: "Draft (unsaved)",
    legacy_bridge: "Legacy Bridge",
  };
  return names[source];
}

/**
 * Get source badge variant for UI styling
 */
export function getLabelSourceVariant(source: LabelSource): "default" | "secondary" | "outline" {
  switch (source) {
    case "draft":
      return "secondary";
    case "overrides":
      return "default";
    case "legacy_bridge":
      return "outline";
    default:
      return "outline";
  }
}
